import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import {
  buildGatewayUrl,
  type GatewayConnectGateway,
  GatewayConnectSection,
} from "@arcadeai/ui-kit/mcp-gateway-connect";
import { Check, Copy, Plug } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useEnginePublicUrl } from "@/app/mcp-gateways/api/get-app-config";

type ConnectPanelProps = {
  gatewaySlug: string;
};

/**
 * Post-save connect step — the payoff of the flow, so it's visually the loudest
 * thing on the page (brand-green framed card). The end-user's personal gateway
 * authenticates with their IdP session (`user_source`), so no API key or user id
 * is surfaced here.
 */
export const ConnectPanel = ({ gatewaySlug }: ConnectPanelProps) => {
  const enginePublicUrl = useEnginePublicUrl();
  const [copied, setCopied] = useState(false);
  const gateway: GatewayConnectGateway = {
    name: "Your tools",
    slug: gatewaySlug,
    auth_type: "user_source",
  };
  const mcpUrl = buildGatewayUrl(enginePublicUrl, gatewaySlug);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(mcpUrl);
    setCopied(true);
    toast.success("MCP URL copied");
    setTimeout(() => setCopied(false), 2000);
  }, [mcpUrl]);

  return (
    <Card className="border-primary/60 bg-primary/5 shadow-lg ring-2 ring-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-brand-text">
          <Plug className="size-5" />
          Your MCP gateway is ready
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          Add this server to your MCP client to start using the tools you saved.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <p className="font-medium text-sm">MCP URL</p>
          <div className="flex items-center gap-2">
            <code className="block flex-1 break-all rounded-md border border-primary/40 bg-background p-3 font-mono text-sm">
              {mcpUrl}
            </code>
            <Button
              aria-label="Copy MCP URL"
              onClick={handleCopy}
              size="icon"
              variant="outline"
            >
              {copied ? (
                <Check className="size-4 text-brand-text" />
              ) : (
                <Copy className="size-4" />
              )}
            </Button>
          </div>
        </div>
        <GatewayConnectSection
          enginePublicUrl={enginePublicUrl}
          gateway={gateway}
        />
      </CardContent>
    </Card>
  );
};
